import type { ButtonSlot } from '@/core/settings';

/**
 * Face-button glyphs as inline SVG rather than text characters.
 *
 * Typographic glyphs are drawn to sit in a line of prose: ✕ is a thin
 * multiplication sign while ▲ ● ■ are solid blocks, so as a set they read as
 * mismatched weights. Drawing them means one deliberate visual weight across
 * all four, and the cross gets a stroke thick enough to hold its own.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const VIEWBOX = 24;

export function createSlotGlyph(slot: ButtonSlot, size: number): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${VIEWBOX} ${VIEWBOX}`);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.appendChild(shapeFor(slot));
  return svg;
}

function shapeFor(slot: ButtonSlot): SVGElement {
  switch (slot) {
    case 'top': {
      // Nudged down a little: a triangle's optical centre sits below its
      // geometric one, so centring it by maths leaves it looking too high.
      const triangle = document.createElementNS(SVG_NS, 'path');
      triangle.setAttribute('d', 'M12 4.5 L20.5 19.5 L3.5 19.5 Z');
      triangle.setAttribute('fill', 'currentColor');
      triangle.setAttribute('stroke', 'currentColor');
      triangle.setAttribute('stroke-width', '2');
      triangle.setAttribute('stroke-linejoin', 'round');
      return triangle;
    }
    case 'right': {
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '8');
      circle.setAttribute('fill', 'currentColor');
      return circle;
    }
    case 'left': {
      const square = document.createElementNS(SVG_NS, 'rect');
      square.setAttribute('x', '4.5');
      square.setAttribute('y', '4.5');
      square.setAttribute('width', '15');
      square.setAttribute('height', '15');
      square.setAttribute('rx', '2');
      square.setAttribute('fill', 'currentColor');
      return square;
    }
    case 'bottom': {
      // Stroked rather than filled, with round caps and a weight tuned to match
      // the mass of the filled shapes.
      const cross = document.createElementNS(SVG_NS, 'path');
      cross.setAttribute('d', 'M6.5 6.5 L17.5 17.5 M17.5 6.5 L6.5 17.5');
      cross.setAttribute('stroke', 'currentColor');
      cross.setAttribute('stroke-width', '5');
      cross.setAttribute('stroke-linecap', 'round');
      cross.setAttribute('fill', 'none');
      return cross;
    }
  }
}
